// src/components/auth/LoginForm.jsx
import { useRef, useState } from "react";
import clsx from "clsx";
import { useLogin } from "../../hooks/useAuth";

export default function LoginForm({ onBack }) {
    const [phone, setPhone] = useState("");
    const [pwd, setPwd] = useState("");

    /* 유효성 */
    const phoneValid = /^\d{8}$/.test(phone); // 010 제외 정확히 8자리
    const pwdValid =
        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^\w\s]).{8,}$/.test(pwd); // 영문+숫자+특수 8자
    const [serverError, setServerError] = useState(""); // 로그인 실패시
    const formValid = phoneValid && pwdValid;
    const loginMutation = useLogin();

    const API_BASE = (
        process.env.REACT_APP_API_URL ?? `http://${window.location.hostname}:8000`
    ).replace(/\/+$/, "");

    const fileRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [faceMsg, setFaceMsg] = useState("");
    const [uploading, setUploading] = useState(false);

    function messageFromError(err) {
        const status = err?.status ?? err?.response?.status;
        const detail =
            err?.detail ?? err?.response?.data?.detail ?? err?.message ?? "";

        if (status === 401) return "전화번호 또는 비밀번호가 올바르지 않습니다.";
        if (status === 422) return "필수 항목이 제대로 입력되지 않았습니다.";
        return detail || "로그인 중 오류가 발생했습니다.";
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formValid || loginMutation.isPending) return;
        console.log(phone);
        console.log(pwd);
        setServerError("");
        loginMutation.mutate(
            {
                phone: "010" + phone,
                password: pwd,
            },
            {
                onSuccess: (data) => {
                    localStorage.setItem("token", data.access_token);
                    window.location.href = "/dashboard";
                },
                onError: (err) => {
                    const msg = messageFromError(err);
                    console.error("로그인 실패:", msg);
                    setServerError(msg);
                },
            }
        );

    };

    const onPickImage = () => {
        setFaceMsg("");
        fileRef.current?.click();
    };

    const onFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setPreviewUrl(URL.createObjectURL(file));
        setFaceMsg("얼굴 인증 중…");

        const fd = new FormData();
        fd.append("image", file);
        // 선택: 폰 입력이 유효하면 후보를 해당 사용자로 좁혀서 서버에 전달
        if (phoneValid) fd.append("phone", "010" + phone);

        try {
            setUploading(true);
            const res = await fetch(`${API_BASE}/auth/face-login`, {
                method: "POST",
                body: fd,
                credentials: "include", // HttpOnly 쿠키 수신
            });

            let data = null;
            try {
                data = await res.json();
            } catch (_) { }

            if (!res.ok) {
                const msg =
                    data?.detail ||
                    data?.message ||
                    (typeof data === "string" ? data : "") ||
                    `얼굴 로그인 실패 (HTTP ${res.status})`;
                setFaceMsg(msg);
                setUploading(false);
                // 같은 파일 재시도 가능하도록 초기화
                e.target.value = "";
                return;
            }

            if (data?.access_token) {
                localStorage.setItem("token", data.access_token);
            }
            setFaceMsg("로그인 성공! 이동합니다…");
            window.location.href = "/dashboard";
        } catch (err) {
            setFaceMsg("요청 중 오류가 발생했습니다.");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const hasAnyError =
        (!!phone && !phoneValid) || (!!pwd && !pwdValid) || !!serverError;

    return (
        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
            {/* 안내 문구 */}
            <div className="pt-9 pb-7">
                <p className="text-2xl md:text-3xl leading-tight">회원 로그인</p>
                <p className="text-sm md:text-lg text-[#4A8AFF] mt-2">
                    전화번호와 비밀번호를 입력해 주세요.
                </p>
            </div>

            {/* 입력 필드 */}
            <div className="flex flex-col gap-6">
                {/* 전화번호 */}
                <div>
                    <label className="block text-sm md:text-lg font-medium mb-1">
                        전화번호
                        {!phoneValid && phone && (
                            <span className="ml-5 text-[#DE4F4F] text-xs md:text-sm">
                                010을 제외한 8자리로 입력해주세요
                            </span>
                        )}
                    </label>

                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => {
                            setPhone(e.target.value.replace(/\D/g, ""));
                            if (serverError) setServerError("");
                        }}
                        maxLength={8}
                        placeholder="010 제외한 전화번호 8자리 입력"
                        aria-invalid={(!phoneValid && !!phone) || !!serverError}
                        className={clsx(
                            "w-full border rounded-lg px-3 py-3.5 bg-[#F1F1F1] outline-none text-sm md:text-lg",
                            ((!phoneValid && phone) || serverError) && "border-[#DE4F4F]",
                            !((!phoneValid && phone) || serverError) && "border-gray-200"
                        )}
                    />
                </div>

                {/* 비밀번호 */}
                <div>
                    <label className="block text-sm md:text-lg font-medium mb-1">
                        비밀번호
                        {!pwdValid && pwd && (
                            <span className="ml-5 text-[#DE4F4F] text-xs md:text-sm">
                                필수 항목이 제대로 입력되지 않았습니다.
                            </span>
                        )}
                    </label>

                    <input
                        type="password"
                        value={pwd}
                        onChange={(e) => {
                            setPwd(e.target.value);
                            if (serverError) setServerError("");
                        }}
                        placeholder="영숫·특수문자 포함 8자 이상"
                        aria-invalid={(!pwdValid && !!pwd) || !!serverError}
                        className={clsx(
                            "w-full border rounded-lg px-3 py-3.5 bg-[#F1F1F1] outline-none text-sm md:text-lg",
                            ((!pwdValid && pwd) || serverError) && "border-[#DE4F4F]",
                            !((!pwdValid && pwd) || serverError) && "border-gray-200"
                        )}
                    />

                    {/* 서버 에러 메시지 (ID/PW) */}
                    {serverError && (
                        <p
                            className="mt-2 text-[#DE4F4F] text-sm md:text-base"
                            role="alert"
                            aria-live="polite"
                        >
                            {serverError}
                        </p>
                    )}
                </div>

                {/* ✅ 얼굴 로그인 (테스트용) */}
                <div className="mt-2">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-px bg-gray-200 flex-1" />
                        <span className="text-xs text-gray-500">또는</span>
                        <div className="h-px bg-gray-200 flex-1" />
                    </div>

                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={onFileChange}
                    />

                    <button
                        type="button"
                        onClick={onPickImage}
                        disabled={uploading}
                        className={clsx(
                            "w-full mt-2 py-3 rounded-xl text-base md:text-lg border",
                            uploading
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-white text-gray-900 hover:bg-gray-50 border-gray-200"
                        )}
                    >
                        {uploading ? "얼굴 인증 중…" : "얼굴로 로그인 (사진 업로드)"}
                    </button>

                    {(previewUrl || faceMsg) && (
                        <div className="mt-3 grid grid-cols-[64px,1fr] gap-3 items-center">
                            {previewUrl ? (
                                <img
                                    src={previewUrl}
                                    alt="preview"
                                    className="w-16 h-16 rounded-lg object-cover border"
                                />
                            ) : (
                                <div className="w-16 h-16 rounded-lg bg-gray-100" />
                            )}
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {faceMsg ||
                                    "선택한 사진으로 얼굴 인증을 시도합니다. 정면 사진을 권장합니다."}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* 버튼 */}
            <div className="mt-auto py-6">
                <button
                    type="submit"
                    disabled={!formValid || loginMutation.isPending}
                    className={clsx(
                        "w-full py-3 md:py-4 rounded-xl text-lg md:text-xl transition-colors",
                        !formValid || loginMutation.isPending
                            ? "bg-[#F1F1F1] text-white cursor-not-allowed"
                            : "bg-[#5F9BFF] text-white hover:bg-[#4A8AFF]"
                    )}
                >
                    {loginMutation.isPending ? "로그인" : "로그인"}
                </button>
            </div>
        </form>
    );
}
